import { Edit as EditIcon } from '@carbon/icons-react';
import type { UserProfile } from '@repo/shared/orpc/types';
import { cx } from 'class-variance-authority';
import { ProfilePhoto } from 'component/people';
import { Button, type ButtonProps, composeRenderProps } from 'react-aria-components';
import { usePersonaKey } from 'util/datastore';
import { makeTestID } from 'util/stdtypes';

import styles from './composer-button.module.css';

export type ComposerButtonProps = Omit<ButtonProps, 'children' | 'isPending' | 'style'> & {
    testID?: string;
    placeholderText: string;
    profile: UserProfile | null;
    hidePhoto?: boolean;
};

export function ComposerButton({
    testID,
    placeholderText,
    profile,
    hidePhoto = false,
    isDisabled,
    className,
    ...rest
}: ComposerButtonProps) {
    const personaKey = usePersonaKey();

    // composeRenderProps: see Dev Notes 'Extending wrapper styles'.
    const composedClassName = composeRenderProps(className, (callerClass) => cx(styles.button, callerClass));

    return (
        <Button {...rest} className={composedClassName} data-testid={makeTestID({ testID, text: placeholderText })} isDisabled={isDisabled}>
            {!hidePhoto && personaKey && (
                <span aria-hidden className={styles.photo}>
                    <ProfilePhoto faint={isDisabled} profile={profile} type='tiny' />
                </span>
            )}
            <span className={styles.text}>{placeholderText}</span>
            <EditIcon aria-hidden className={styles.icon} size={24} />
        </Button>
    );
}
